import { json_headers, request } from './common';
import type { RequestOptions, SearchServiceEnvelope } from './common';

export type ReproduceSearchParams = {
  startDate: string;
  endDate: string;
  searchId: string;
};

type RawSearchEntityLocation = {
  latitude: number;
  longitude: number;
};

type RawSearchEntity = {
  id: string;
  name: string;
  location: RawSearchEntityLocation | null;
};

type RawSearchFilter = {
  type: string | null;
  label: string | null;
  value: string | null;
};

type RawSearchReproductionRecord = {
  brand: string | null;
  region: string | null;
  platform: string | null;
  language: string | null;
  search_type: string | null;
  destinations: Array<RawSearchEntity>;
  landmarks: Array<RawSearchEntity>;
  properties: Array<RawSearchEntity>;
  start_date: string | null;
  end_date: string | null;
  date_type: string | null;
  duration_type: string | null;
  flexible_months: Array<string>;
  adults: number | null;
  children: number | null;
  sort_value: string | null;
  luxplus_tier: string | null;
  loyalty_tier: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  natural_language_query: string | null;
  filters: Array<RawSearchFilter>;
};

function searchAdminSearchesMetaPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/meta`;
}

function searchAdminSearchesImpressionsPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/impressions`;
}

export type SearchReproductionFilter = {
  type: string | null;
  label: string | null;
  value: string | null;
};

export type SearchEntityLocation = {
  latitude: number;
  longitude: number;
};

export type SearchReproductionEntity = {
  id: string;
  name: string;
  location: SearchEntityLocation | null;
};

function normaliseSearchEntities(entities: Array<RawSearchEntity>): Array<SearchReproductionEntity> {
  return entities.map((entity) => ({
    id: entity.id,
    name: entity.name,
    location:
      entity.location == null ? null : { latitude: entity.location.latitude, longitude: entity.location.longitude },
  }));
}

export type SearchReproductionRow = {
  brand: string | null;
  region: string | null;
  platform: string | null;
  language: string | null;
  searchType: string | null;
  destinations: Array<SearchReproductionEntity>;
  landmarks: Array<SearchReproductionEntity>;
  properties: Array<SearchReproductionEntity>;
  startDate: string | null;
  endDate: string | null;
  dateType: string | null;
  durationType: string | null;
  flexibleMonths: Array<string>;
  adults: number | null;
  children: number | null;
  sortValue: string | null;
  luxplusTier: string | null;
  loyaltyTier: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  naturalLanguageQuery: string | null;
  filters: Array<SearchReproductionFilter>;
};

export function normaliseSearchReproduction(row: RawSearchReproductionRecord): SearchReproductionRow {
  return {
    brand: row.brand ?? null,
    region: row.region ?? null,
    platform: row.platform ?? null,
    language: row.language ?? null,
    searchType: row.search_type ?? null,
    destinations: normaliseSearchEntities(row.destinations ?? []),
    landmarks: normaliseSearchEntities(row.landmarks ?? []),
    properties: normaliseSearchEntities(row.properties ?? []),
    startDate: row.start_date ?? null,
    endDate: row.end_date ?? null,
    dateType: row.date_type ?? null,
    durationType: row.duration_type ?? null,
    flexibleMonths: row.flexible_months ?? [],
    adults: row.adults ?? null,
    children: row.children ?? null,
    sortValue: row.sort_value ?? null,
    luxplusTier: row.luxplus_tier ?? null,
    loyaltyTier: row.loyalty_tier ?? null,
    utmSource: row.utm_source ?? null,
    utmMedium: row.utm_medium ?? null,
    utmCampaign: row.utm_campaign ?? null,
    naturalLanguageQuery: row.natural_language_query ?? null,
    filters: (row.filters ?? []).map((filter) => ({
      type: filter.type,
      label: filter.label,
      value: filter.value,
    })),
  };
}

export async function getSearchReproduction(
  params: ReproduceSearchParams,
  options: RequestOptions = {},
): Promise<SearchReproductionRow | null> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    searchId: params.searchId,
  }).toString();

  const response = (await request(`${searchAdminSearchesMetaPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<RawSearchReproductionRecord | null>;

  return response.result ? normaliseSearchReproduction(response.result) : null;
}

type RawSearchImpressionRow = {
  offer_id: string | null;
  category_id: string | null;
  product_id: string | null;
  product_available: boolean | null;
  region: string | null;
};

type RawSearchImpressionsResponse = Array<RawSearchImpressionRow>;

export type SearchImpressionRow = {
  offerId: string | null;
  categoryId: string | null;
  productId: string | null;
  productAvailable: boolean | null;
  region: string | null;
};

function normaliseImpressionRows(response: RawSearchImpressionsResponse): Array<SearchImpressionRow> {
  return response.map((row) => ({
    offerId: row.offer_id,
    categoryId: row.category_id,
    productId: row.product_id,
    productAvailable: row.product_available,
    region: row.region,
  }));
}

export async function getSearchImpressions(
  params: ReproduceSearchParams,
  options: RequestOptions = {},
): Promise<Array<SearchImpressionRow>> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    searchId: params.searchId,
  }).toString();

  const response = (await request(`${searchAdminSearchesImpressionsPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<RawSearchImpressionsResponse>;

  return normaliseImpressionRows(response.result);
}
